"use client";

import { useAccessToken } from "@/hooks/useAccessToken";
import { authorizedGet } from "@/utils/request";
import { useEffect, useMemo, useState } from "react";
import { FrostBorderCard } from "../FrostBorderCard";
import { PRIZE_IMAGE_MAP } from "./PrizeWheelUtils";

type OverallPrize = {
  name: string;
  wallet_address: string;
  tier: number;
};

type OverallPrizesResponse = {
  result?: OverallPrize[];
};

function resolvePrizeImage(name: string): string | undefined {
  const lower = name.toLowerCase();
  if (lower.includes("gems")) return PRIZE_IMAGE_MAP["500 gems"];
  if (lower.includes("imx")) return PRIZE_IMAGE_MAP["50 imx"];
  if (lower.includes("$")) return PRIZE_IMAGE_MAP["gift card"];

  const directKey = lower as keyof typeof PRIZE_IMAGE_MAP;
  if (PRIZE_IMAGE_MAP[directKey]) return PRIZE_IMAGE_MAP[directKey];

  return "/icons/prize-wheel/generic-gem.webp";
}

function getDisplayName(name: string): string {
  const lower = name.toLowerCase();
  if (lower.includes("series x")) return "XBox Series X";
  if (lower.includes("switch 2")) return "Nintendo Switch 2";
  if (lower.includes("playstation 5")) return "Playstation 5";
  if (lower.includes("gems")) return "Gems";
  if (lower.includes("imx")) return "$IMX";
  if (lower.includes("$")) return "GiftCards";
  return name;
}

function getBucketLabel(name: string): string {
  const lower = name.toLowerCase();
  if (lower.includes("gems")) return "Gems";
  if (lower.includes("imx")) return "$IMX";
  if (lower.includes("$")) return "Gift Cards";
  return getDisplayName(name);
}

export function WonPrizesSnapshot({ className }: { className?: string }) {
  const [items, setItems] = useState<OverallPrize[]>([]);
  const [loading, setLoading] = useState(false);
  const getAccessToken = useAccessToken();

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const token = await getAccessToken();
        if (!token) return;
        const data = await authorizedGet<OverallPrizesResponse>(
          "/v2/rewards/spin/overall-prizes-won",
          token,
        );

        setItems(data.result ?? []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [getAccessToken]);

  const normalize = (s: string) => s.toLowerCase().trim();

  const highValuePrizes = useMemo(
    () => items.filter((p) => p.tier === 1) ?? [],
    [items],
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const standardPrizes = useMemo(() => {
    const randPick = (arr: OverallPrize[]) =>
      arr.length > 0 ? arr[Math.floor(Math.random() * arr.length)] : undefined;

    const gems = items.filter((p) => normalize(p.name).includes("gems"));
    const imx = items.filter((p) => normalize(p.name).includes("imx"));
    const gift = items.filter((p) => normalize(p.name).includes("$"));

    const picks = [randPick(gems), randPick(imx), randPick(gift)].filter(
      Boolean,
    ) as OverallPrize[];
    return picks;
  }, [items]);

  return (
    <FrostBorderCard className="px-6 md:px-10 py-8 md:py-10" minHeight={720}>
      <div className="flex flex-col gap-2 w-full">
        <div className="flex items-center w-full">
          <h3 className="text-lg md:text-xl font-semibold mb-2 text-left">
            Join Thousands of People Winning on the Prize Wheel.
          </h3>
        </div>
        <div className="flex items-center w-full">
          <p className="text-[12px] leading-4 text-[#b6b6b6]">
            Every Week we’re giving away Legendary Prizes for players completing
            in-game quests and referring friends to Immutable Play.
            <br />
            Take a look at some of the recent winners
          </p>
        </div>
      </div>

      <div className="py-10 flex flex-col gap-10 w-full">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-12 w-full">
          {highValuePrizes.map((p) => {
            const img = resolvePrizeImage(p.name);
            return (
              <div
                key={`${p.name}-${p.wallet_address}`}
                className="flex-1 flex flex-col items-center gap-2"
              >
                <div className="w-full aspect-square overflow-hidden">
                  {img ? (
                    <img
                      src={img}
                      alt={p.name}
                      className="w-full h-full object-contain scale-[1.05]"
                    />
                  ) : (
                    <div className="w-full h-full bg-white/10" />
                  )}
                </div>
                <p className="text-neutral-50 text-sm md:text-base font-bold leading-tight text-center whitespace-normal break-words">
                  {getDisplayName(p.name)}
                </p>
                <p className="text-[#b6b6b6] text-[10px] leading-[14px] tracking-[0.1px] text-center">
                  {p.wallet_address}
                </p>
              </div>
            );
          })}
        </div>

        <div className="h-px w-full opacity-25 bg-white/20" />

        {/* Remaining 3 category tiles (Gems, IMX, GiftCards) */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-10 md:gap-12 w-full items-start justify-items-center">
          {standardPrizes.map((p) => {
            const img = resolvePrizeImage(p.name);
            return (
              <div
                key={`${p.name}-${p.wallet_address}`}
                className="flex-1 flex flex-col items-center gap-2"
              >
                <div className="size-24 md:size-28 overflow-hidden flex items-center justify-center">
                  {img ? (
                    <img
                      src={img}
                      alt={p.name}
                      width={112}
                      height={112}
                      className="w-28 h-28 object-contain"
                    />
                  ) : (
                    <div className="w-28 h-28 bg-white/10" />
                  )}
                </div>
                <div className="flex flex-col items-start w-full gap-1">
                  <p className="text-neutral-50 text-base md:text-lg leading-none text-center w-full">
                    {getBucketLabel(p.name)}
                  </p>
                  <p className="text-[#b6b6b6] text-[11px] md:text-[12px] leading-[16px] tracking-[0.1px] text-center w-full">
                    {p.wallet_address}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {loading && (
        <div className="text-xs text-white/70">Loading recent winners…</div>
      )}
    </FrostBorderCard>
  );
}

// Named exports only per project conventions
